import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import {
  Field,
  Label,
  Radio,
  RadioGroup,
  Select,
  FluentProvider,
  webLightTheme,
  Input,
} from '@fluentui/react-components';
import React from 'react';
import { Controller, FieldError, UseFormMethods } from 'react-hook-form';
import { RequiredMarker } from '../../common/formFields/RequiredMarker';
import RequiredFieldsNotification from '../RequiredNotification';
import CompanyContactsPicker from './CompanyContactsPicker';
import {
  CompanyCreationContactType,
  ICompanyCreationContact,
  ISubledgerInfo,
  RequiredFieldsType,
} from './types';
import IDropdownOption from '../../../utils/types/IDropdownOption';
import { yesOption, noOption } from '../../../utils/types/CommonDropdownOptions';

interface ISubledgerInfoFormProps {
  form: UseFormMethods<ISubledgerInfo>;
  data: ISubledgerInfo;
}

const requiredLabelStyles = { paddingBottom: 0 };

const workSchedule: IDropdownOption[] = [
  { key: null, text: '--' },
  { key: 48, text: '48' },
  { key: 44, text: '44' },
  { key: 42.5, text: '42.5' },
  { key: 42, text: '42' },
  { key: 40, text: '40' },
  { key: 39, text: '39' },
  { key: 38.75, text: '38.75' },
  { key: 38.5, text: '38.5' },
  { key: 37.5, text: '37.5' },
  { key: 37, text: '37' },
];

const SubledgerInfoForm = ({ form, data }: ISubledgerInfoFormProps): JSX.Element => {
  const { control, errors, setValue, watch } = form;

  const willHaveHeadcount = watch('willHaveHeadcount', data?.willHaveHeadcount === true);

  const updateForm = (
    formKey: keyof ISubledgerInfo,
    contacts: ICompanyCreationContact[],
    contactType: CompanyCreationContactType,
  ): void => {
    const newContacts = [] as ICompanyCreationContact[];
    contacts.forEach((contact) => {
      contact.contactType = contactType;
      newContacts.push(contact);
    });
    setValue(formKey, newContacts[0]);
  };

  return (
    <FluentProvider theme={webLightTheme}>
      <Stack tokens={{ childrenGap: 10 }}>
        <RequiredFieldsNotification type={RequiredFieldsType.SubledgerSetup} />
        <Controller
          name="isDomesticEntity"
          defaultValue={data?.isDomesticEntity}
          control={control}
          render={({ value }): React.ReactElement => {
            return (
              <Field label="Is this a new domestic entity?" required>
                <RadioGroup
                  id="isDomesticEntity"
                  name="isDomesticEntity"
                  value={value?.toString().toLowerCase()}
                  onChange={(e, radioData): void => {
                    setValue('isDomesticEntity', radioData.value === 'true');
                  }}
                >
                  <Radio value={yesOption.key} label={yesOption.text} />
                  <Radio value={noOption.key} label={noOption.text} />
                </RadioGroup>
              </Field>
            );
          }}
        />
        <Controller
          name="willHaveIntercompanyActivity"
          defaultValue={data?.willHaveIntercompanyActivity}
          control={control}
          render={({ value }): React.ReactElement => {
            return (
              <Field
                label="Will the company have inter-company transactions with other Microsoft entities?"
                required
              >
                <RadioGroup
                  id="willHaveIntercompanyActivity"
                  name="willHaveIntercompanyActivity"
                  value={value?.toString().toLowerCase()}
                  onChange={(e, radioData): void => {
                    setValue('willHaveIntercompanyActivity', radioData.value === 'true');
                  }}
                >
                  <Radio value={yesOption.key} label={yesOption.text} />
                  <Radio value={noOption.key} label={noOption.text} />
                </RadioGroup>
              </Field>
            );
          }}
        />
        <Controller
          name="willRequireMscPremierServices"
          defaultValue={data?.willRequireMscPremierServices}
          control={control}
          render={({ value }): React.ReactElement => {
            return (
              <Field label="Will the company require billing for MSC/Premier Services?" required>
                <RadioGroup
                  id="willRequireMscPremierServices"
                  name="willRequireMscPremierServices"
                  value={value?.toString().toLowerCase()}
                  onChange={(e, radioData): void => {
                    setValue('willRequireMscPremierServices', radioData.value === 'true');
                  }}
                >
                  <Radio value={yesOption.key} label={yesOption.text} />
                  <Radio value={noOption.key} label={noOption.text} />
                </RadioGroup>
              </Field>
            );
          }}
        />
        <Controller
          name="willHaveHeadcount"
          defaultValue={data?.willHaveHeadcount}
          control={control}
          render={({ value }): React.ReactElement => {
            return (
              <Field label="Will the company have headcount?" required>
                <RadioGroup
                  id="willHaveHeadcount"
                  name="willHaveHeadcount"
                  value={value?.toString().toLowerCase()}
                  onChange={(e, radioData): void => {
                    setValue('willHaveHeadcount', radioData.value === 'true');
                    if (radioData.value === 'false') {
                      setValue('personalSubareaCities', '');
                      setValue('weeklyWorkingHours', null);
                    }
                  }}
                >
                  <Radio value={yesOption.key} label={yesOption.text} />
                  <Radio value={noOption.key} label={noOption.text} />
                </RadioGroup>
              </Field>
            );
          }}
        />
        <Controller
          control={control}
          name="personalSubareaCities"
          defaultValue={data?.personalSubareaCities || ''}
          render={({ onChange, onBlur, value, name, ref }) => (
            <Field
              label={
                <Label style={requiredLabelStyles}>
                  Personnel Subarea {willHaveHeadcount ? <RequiredMarker /> : null}
                </Label>
              }
              validationMessage={errors?.personalSubareaCities?.message}
            >
              <Input
                id="personalSubareaCities"
                value={value}
                name={name}
                ref={ref}
                onBlur={onBlur}
                onChange={(ev, inputData) => onChange(inputData.value)}
                disabled={willHaveHeadcount === false}
                aria-label="Personnel Subarea Cities"
              />
            </Field>
          )}
        />
        <Controller
          name="weeklyWorkingHours"
          control={control}
          defaultValue={data?.weeklyWorkingHours}
          render={({ value }): React.ReactElement => (
            <>
              <Label style={requiredLabelStyles}>
                Work Schedule (# of hours per week) {willHaveHeadcount ? <RequiredMarker /> : null}
              </Label>
              <Select
                onChange={(event: React.ChangeEvent<HTMLSelectElement>): void => {
                  const selectedValue = event.target.value;
                  setValue(
                    'weeklyWorkingHours',
                    selectedValue === '' ? null : Number(selectedValue),
                  );
                }}
                aria-label="Work Schedule"
                disabled={willHaveHeadcount === false}
                value={value?.toString() || ''}
              >
                <option value="">Select an option</option>
                {workSchedule.map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.text}
                  </option>
                ))}
              </Select>
            </>
          )}
        />
        <Controller
          name="willNeedFaModule"
          defaultValue={data?.willNeedFaModule}
          control={control}
          render={({ value }): React.ReactElement => {
            return (
              <Field label="Will the company need the FA module?" required>
                <RadioGroup
                  id="willNeedFaModule"
                  name="willNeedFaModule"
                  value={value?.toString().toLowerCase()}
                  onChange={(e, radioData): void => {
                    setValue('willNeedFaModule', radioData.value === 'true');
                  }}
                >
                  <Radio value={yesOption.key} label={yesOption.text} />
                  <Radio value={noOption.key} label={noOption.text} />
                </RadioGroup>
              </Field>
            );
          }}
        />
        <Controller
          name="willNeedApMmModule"
          defaultValue={data?.willNeedApMmModule}
          control={control}
          render={({ value }): React.ReactElement => {
            return (
              <Field label="Will the company need the AP/MM module?" required>
                <RadioGroup
                  id="willNeedApMmModule"
                  name="willNeedApMmModule"
                  value={value?.toString().toLowerCase()}
                  onChange={(e, radioData): void => {
                    setValue('willNeedApMmModule', radioData.value === 'true');
                  }}
                >
                  <Radio value={yesOption.key} label={yesOption.text} />
                  <Radio value={noOption.key} label={noOption.text} />
                </RadioGroup>
              </Field>
            );
          }}
        />
        <Controller
          name="willTransmitToMsSales"
          defaultValue={data?.willTransmitToMsSales}
          control={control}
          render={({ value }): React.ReactElement => {
            return (
              <Field label="Will the new company transmit sales to MS Sales?" required>
                <RadioGroup
                  id="willTransmitToMsSales"
                  name="willTransmitToMsSales"
                  value={value?.toString().toLowerCase()}
                  onChange={(e, radioData): void => {
                    setValue('willTransmitToMsSales', radioData.value === 'true');
                  }}
                >
                  <Radio value={yesOption.key} label={yesOption.text} />
                  <Radio value={noOption.key} label={noOption.text} />
                </RadioGroup>
              </Field>
            );
          }}
        />
        <Controller
          name="bsReconOwner"
          valueKey="bsReconOwner"
          control={control}
          defaultValue={data?.bsReconOwner}
          render={() => {
            return (
              <>
                <Label htmlFor="bsReconOwnerPicker" style={requiredLabelStyles}>
                  Balance Sheet Recon Owner <RequiredMarker />
                </Label>
                <CompanyContactsPicker
                  controlName="bsReconOwner"
                  removeButtonAriaLabel="Remove Balance Sheet Recon Owner"
                  onChange={(contacts: ICompanyCreationContact[]): void => {
                    updateForm('bsReconOwner', contacts, CompanyCreationContactType.BsReconOwner);
                  }}
                  itemLimit={1}
                  errorMessage={(errors?.bsReconOwner as unknown as FieldError)?.message}
                  defaultValue={data?.bsReconOwner ? [{ ...data?.bsReconOwner }] : []}
                />
              </>
            );
          }}
        />
        <Controller
          name="bsReconReviewer"
          valueKey="bsReconReviewer"
          control={control}
          defaultValue={data?.bsReconReviewer}
          render={() => {
            return (
              <>
                <Label htmlFor="bsReconReviewerPicker" style={requiredLabelStyles}>
                  Balance Sheet Recon Reviewer <RequiredMarker />
                </Label>
                <CompanyContactsPicker
                  controlName="bsReconReviewer"
                  removeButtonAriaLabel="Remove Balance Sheet Recon Reviewer"
                  onChange={(contacts: ICompanyCreationContact[]): void => {
                    updateForm(
                      'bsReconReviewer',
                      contacts,
                      CompanyCreationContactType.BsReconReviewer,
                    );
                  }}
                  itemLimit={1}
                  errorMessage={(errors?.bsReconReviewer as unknown as FieldError)?.message}
                  defaultValue={data?.bsReconReviewer ? [{ ...data?.bsReconReviewer }] : []}
                />
              </>
            );
          }}
        />
        <Controller
          name="bsReconController"
          valueKey="bsReconController"
          control={control}
          defaultValue={data?.bsReconController}
          render={() => {
            return (
              <>
                <Label htmlFor="bsReconControllerPicker" style={requiredLabelStyles}>
                  Balance Sheet Recon Controller <RequiredMarker />
                </Label>
                <CompanyContactsPicker
                  controlName="bsReconController"
                  removeButtonAriaLabel="Remove Balance Sheet Recon Controller"
                  onChange={(contacts: ICompanyCreationContact[]): void => {
                    updateForm(
                      'bsReconController',
                      contacts,
                      CompanyCreationContactType.BsReconController,
                    );
                  }}
                  itemLimit={1}
                  errorMessage={(errors?.bsReconController as unknown as FieldError)?.message}
                  defaultValue={data?.bsReconController ? [{ ...data?.bsReconController }] : []}
                />
              </>
            );
          }}
        />
      </Stack>
    </FluentProvider>
  );
};

export default SubledgerInfoForm;
